import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ShoppingBag, Tag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { items, removeItem, subtotal } = useCart();
  const nav = useNavigate();
  const [code, setCode] = useState(localStorage.getItem("cp_coupon") || "");
  const [applied, setApplied] = useState(() => { try { return JSON.parse(localStorage.getItem("cp_coupon_data")); } catch { return null; } });
  const [loading, setLoading] = useState(false);

  const discount = applied?.discount || 0;
  const total = Math.max(subtotal - discount, 0);

  const applyCoupon = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setApplied(data);
      localStorage.setItem("cp_coupon", data.code);
      localStorage.setItem("cp_coupon_data", JSON.stringify(data));
      toast.success("Code promo appliqué !");
    } catch (e) {
      setApplied(null); localStorage.removeItem("cp_coupon"); localStorage.removeItem("cp_coupon_data");
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };

  if (items.length === 0) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <ShoppingBag className="h-12 w-12 text-line mx-auto mb-4" />
      <h1 className="font-heading text-3xl text-ink">Votre panier est vide</h1>
      <p className="text-ink-muted mt-3">Découvrez notre collection d'eBooks pour parents.</p>
      <Button asChild className="mt-6 bg-terracotta hover:bg-terracotta-dark text-white rounded-md"><Link to="/boutique">Voir la boutique</Link></Button>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-heading text-4xl text-ink tracking-tight mb-8">Votre panier</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4 bg-cream border border-line rounded-lg p-4" data-testid={`cart-item-${it.slug}`}>
              <img src={it.cover_url} alt={it.title} className="w-16 h-20 object-contain rounded-sm bg-sand" />
              <div className="flex-1">
                <h3 className="font-heading text-lg text-ink">{it.title}</h3>
                <p className="text-sm text-ink-muted">{it.is_pack ? "Pack — 3 eBooks" : "eBook numérique (PDF)"}</p>
                <p className="text-terracotta font-semibold mt-1">{formatPrice(it.price)}</p>
              </div>
              <button onClick={() => removeItem(it.product_id)} className="text-ink-muted hover:text-destructive self-start p-1" data-testid={`remove-${it.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="bg-cream border border-line rounded-lg p-6 h-fit">
          <h2 className="font-heading text-xl text-ink mb-4">Récapitulatif</h2>
          <div className="flex gap-2 mb-4">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code promo" className="bg-sand border-line" data-testid="coupon-input" />
            <Button variant="outline" onClick={applyCoupon} disabled={loading} className="border-line" data-testid="apply-coupon"><Tag className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-2 text-sm border-t border-line pt-4">
            <div className="flex justify-between text-ink-muted"><span>Sous-total</span><span>{formatPrice(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-sage"><span>Réduction ({applied.code})</span><span>-{formatPrice(discount)}</span></div>}
            <div className="flex justify-between text-ink font-semibold text-lg pt-2 border-t border-line"><span>Total</span><span data-testid="cart-total">{formatPrice(total)}</span></div>
            <p className="text-xs text-ink-muted">TVA incluse le cas échéant.</p>
          </div>
          <Button onClick={() => nav("/checkout")} className="w-full mt-5 bg-terracotta hover:bg-terracotta-dark text-white rounded-md" data-testid="go-to-checkout">
            Passer au paiement <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
